// Theme: System / Light / Dark, persisted, applied app-wide.
//
// How theming works here: this provider does exactly one visual thing — it sets
// `data-theme="light|dark"` on <html>. Every color in the app is a CSS custom
// property defined for both themes in index.css, so that single attribute
// retheme's every screen at paint time with no re-render and no prop drilling.
// Components should reference `var(--token)`, never a raw hex.
//
// The pieces that CAN'T read CSS variables — the native status bar, the
// Capacitor web view background, and the browser's theme-color meta — are
// updated imperatively from THEME_CHROME below.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'nomad.theme';

/** Colors the native shell needs as literal values, mirroring index.css. */
export const THEME_CHROME = {
  light: { ground: '#EAF1FB', band: '#0B1B33' },
  dark:  { ground: '#0B1B33', band: '#0B1B33' },
};

const ThemeContext = createContext({
  preference: 'light',
  theme: 'light',
  setPreference: () => {},
  toggleTheme: () => {},
  isDark: false,
});

/**
 * The theme a device gets before anyone has chosen one.
 *
 * Deliberately 'light', NOT 'system': Nomad is a light-first product and the
 * light palette is the one the design was drawn against, so a first-run user on
 * a phone set to dark should still see the app the way it's meant to look.
 * 'system' remains available — it's just opt-in from Settings now, rather than
 * the thing that decides for you.
 */
const DEFAULT_PREFERENCE = 'light';

function readStoredPreference() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  } catch (_) {
    // Private mode / storage disabled — fall through to the default.
  }
  return DEFAULT_PREFERENCE;
}

function systemPrefersDark() {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch (_) {
    return false;
  }
}

/** Resolve a preference into an actual theme. */
function resolve(preference) {
  if (preference === 'light' || preference === 'dark') return preference;
  return systemPrefersDark() ? 'dark' : 'light';
}

/**
 * Paint the resolved theme onto <html> and the native chrome.
 *
 * Exported (and called once from index.js before React mounts) so the very
 * first frame is already in the right theme — otherwise a dark-mode user sees
 * a white flash while the bundle boots.
 */
export function applyTheme(theme) {
  const chrome = THEME_CHROME[theme] || THEME_CHROME.light;

  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;

  // The top of every screen is the ink header band, so the browser/OS chrome
  // color is ink in BOTH themes — not the page ground.
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', chrome.band);

  // iOS reads this one for the standalone status bar. 'black-translucent' keeps
  // white glyphs over our ink band.
  const iosBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
  if (iosBar) iosBar.setAttribute('content', 'black-translucent');

  // Native shell (no-op on web). Status bar text stays light in both themes
  // because it sits on the ink band either way.
  applyNativeChrome(chrome);
}

async function applyNativeChrome(chrome) {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (!Capacitor.isNativePlatform()) return;

    try {
      const { StatusBar, Style } = await import('@capacitor/status-bar');
      // Style.Dark = "dark background, light content" — white glyphs on ink.
      await StatusBar.setStyle({ style: Style.Dark });
      if (Capacitor.getPlatform() === 'android') {
        await StatusBar.setBackgroundColor({ color: chrome.band });
      }
    } catch (_) {}

    // Overscroll / rubber-band area behind the web view.
    try {
      document.body.style.backgroundColor = chrome.ground;
    } catch (_) {}
  } catch (_) {}
}

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(readStoredPreference);
  const [theme, setTheme] = useState(() => resolve(readStoredPreference()));

  // Apply on every change.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Follow the OS while the preference is 'system'. The listener is torn down
  // as soon as the user picks an explicit theme, so a system change can't
  // override a deliberate choice.
  useEffect(() => {
    if (preference !== 'system') {
      setTheme(preference);
      return undefined;
    }
    setTheme(systemPrefersDark() ? 'dark' : 'light');

    let mq;
    try {
      mq = window.matchMedia('(prefers-color-scheme: dark)');
    } catch (_) {
      return undefined;
    }
    const onChange = (e) => setTheme(e.matches ? 'dark' : 'light');

    // Safari < 14 only has the deprecated addListener form, and the iOS web
    // view can be old on older devices.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, [preference]);

  const setPreference = useCallback((next) => {
    if (next !== 'light' && next !== 'dark' && next !== 'system') return;
    setPreferenceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {}
  }, []);

  // The one-tap flip behind the profile header button. It always writes an
  // EXPLICIT preference — so tapping it once while on 'system' also detaches
  // the app from the phone's setting, which is what someone reaching for a
  // light/dark button is asking for.
  const toggleTheme = useCallback(() => {
    setPreference(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setPreference]);

  const value = useMemo(
    () => ({ preference, theme, setPreference, toggleTheme, isDark: theme === 'dark' }),
    [preference, theme, setPreference, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}

/**
 * Read the resolved theme without subscribing to the context.
 *
 * For the handful of places that need a literal value at call time rather than
 * a CSS variable — canvas drawing, an inline SVG gradient stop computed in JS,
 * a third-party widget's config object.
 */
export function currentTheme() {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}
